import { useEffect, useRef, useCallback } from 'react'
import { getSocket, disconnectWebSocket } from '../lib/websocket'
import type { Socket } from 'socket.io-client'

interface UseWebSocketOptions {
  onConnect?: () => void
  onDisconnect?: () => void
  onError?: (error: Error) => void
}

/**
 * Hook para manejar conexión WebSocket
 */
export const useWebSocket = (options: UseWebSocketOptions = {}) => {
  const socketRef = useRef<Socket | null>(null)
  const { onConnect, onDisconnect, onError } = options

  useEffect(() => {
    const socket = getSocket()
    if (!socket) {
      return
    }

    socketRef.current = socket

    const handleConnect = () => {
      onConnect?.()
    }

    const handleDisconnect = () => {
      onDisconnect?.()
    }

    const handleError = (error: Error) => {
      onError?.(error)
    }

    socket.on('connect', handleConnect)
    socket.on('disconnect', handleDisconnect)
    socket.on('error', handleError)

    return () => {
      socket.off('connect', handleConnect)
      socket.off('disconnect', handleDisconnect)
      socket.off('error', handleError)
    }
  }, [onConnect, onDisconnect, onError])

  return socketRef.current
}

/**
 * Hook para suscribirse a actualizaciones de pedidos
 */
export const useOrderUpdates = (
  storeId: string | undefined,
  onUpdate: (order: any) => void,
  onCreate?: (order: any) => void
) => {
  const socketRef = useRef<Socket | null>(null)

  useEffect(() => {
    if (!storeId) return

    const socket = getSocket()
    if (!socket) return

    socketRef.current = socket

    // Suscribirse a pedidos del store
    socket.emit('subscribe:orders', { storeId })

    const handleUpdate = (order: any) => {
      onUpdate(order)
    }

    const handleCreate = (order: any) => {
      onCreate?.(order)
    }

    socket.on('order:updated', handleUpdate)
    socket.on('order:created', handleCreate)

    return () => {
      socket.off('order:updated', handleUpdate)
      socket.off('order:created', handleCreate)
      socket.emit('unsubscribe:orders', { storeId })
    }
  }, [storeId, onUpdate, onCreate])
}

/**
 * Hook para suscribirse a un pedido específico
 */
export const useOrderDetail = (
  orderId: string | undefined,
  onUpdate: (order: any) => void
) => {
  const socketRef = useRef<Socket | null>(null)

  useEffect(() => {
    if (!orderId) return

    const socket = getSocket()
    if (!socket) return

    socketRef.current = socket

    // Suscribirse al pedido específico
    socket.emit('subscribe:order', { orderId })

    const handleUpdate = (order: any) => {
      onUpdate(order)
    }

    socket.on('order:updated', handleUpdate)

    return () => {
      socket.off('order:updated', handleUpdate)
      socket.emit('unsubscribe:order', { orderId })
    }
  }, [orderId, onUpdate])
}

/**
 * Hook para suscribirse a estadísticas
 */
export const useStatsUpdates = (
  storeId: string | undefined,
  onUpdate: (stats: any) => void
) => {
  const socketRef = useRef<Socket | null>(null)

  useEffect(() => {
    if (!storeId) return

    const socket = getSocket()
    if (!socket) return

    socketRef.current = socket

    // Suscribirse a estadísticas del store
    socket.emit('subscribe:stats', { storeId })

    const handleUpdate = (stats: any) => {
      onUpdate(stats)
    }

    socket.on('stats:updated', handleUpdate)

    return () => {
      socket.off('stats:updated', handleUpdate)
      socket.emit('unsubscribe:stats', { storeId })
    }
  }, [storeId, onUpdate])
}

